import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { RtLogger } from './rt-logger';

export function defaultRtDebugMeasureCallback(
  signature: string,
  duration: number,
  async: boolean = false,
): void {
  RtLogger.measure(signature.split('.'), duration, async);
}

export function RtDebugMeasure(): (
  target: any,
  methodName: string,
) => { value: (...args: any[]) => Function } {
  return function (target: any, methodName: string): { value: (...args: any[]) => Function } {
    const copy = { ...target };
    const method: Function = copy[methodName];
    const signature = `${target.constructor.name}.${methodName}`;

    return {
      value: function (...args: any[]): Observable<any> | Promise<any> | any {
        return rtDebugMeasure(method, args, this, signature, defaultRtDebugMeasureCallback);
      },
    };
  };
}

export function rtDebugMeasure<T>(
  fn: T & Function,
  context: any = null,
  args: any[] | any = [],
  signature: string = 'Function call',
  callback: (
    signature: string,
    duration: number,
    async: boolean,
  ) => void = defaultRtDebugMeasureCallback,
): Observable<T> | Promise<T> | T {
  if (!fn) {
    throw new Error(`rtDebugMeasure(${signature})\nNothing to measure, provided: '${fn}'.`);
  }

  const startTime = performance.now();
  const result = fn.apply(context, args);
  const syncResultEnd = performance.now() - startTime;

  // maybe Promise ?
  if (result && result.then) {
    return (<Promise<any>>result).then(promiseResult => {
      callback.apply(context, [signature, performance.now() - startTime, true]);
      return Promise.resolve(promiseResult);
    });
  }

  // maybe Observable ?
  if (result && result.subscribe) {
    return (<Observable<any>>result).pipe(
      tap(null, null, () => {
        callback.apply(context, [signature, performance.now() - startTime, true]);
      }),
    );
  }

  callback.apply(context, [signature, syncResultEnd, false]);
  return result;
}
